import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ArrowUpRight, Github, ArrowLeft, ChevronDown } from "lucide-react";
import { AskAboutButton } from "./AskAboutButton";
import ReactMarkdown from "react-markdown";

const PALETTE = [
  { color: "#22d3ee", accent: "rgba(34,211,238,0.08)", border: "rgba(34,211,238,0.2)" },
  { color: "#a78bfa", accent: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.2)" },
  { color: "#f97316", accent: "rgba(249,115,22,0.08)", border: "rgba(249,115,22,0.2)" },
  { color: "#86efac", accent: "rgba(134,239,172,0.08)", border: "rgba(134,239,172,0.2)" },
  { color: "#fb7185", accent: "rgba(251,113,133,0.08)", border: "rgba(251,113,133,0.2)" },
  { color: "#fbbf24", accent: "rgba(251,191,36,0.08)", border: "rgba(251,191,36,0.2)" },
  { color: "#818cf8", accent: "rgba(129,140,248,0.08)", border: "rgba(129,140,248,0.2)" },
  { color: "#34d399", accent: "rgba(52,211,153,0.08)", border: "rgba(52,211,153,0.2)" },
];

const FALLBACK_PROJECTS = [
  {
    id: 1, title: "VSCode Portfolio",
    short_description: "Site portfolio interactif reproduisant l'interface de VS Code.",
    long_description: "",
    technologies: [{ name: "React" }, { name: "TypeScript" }],
    live_url: "#", github_url: "#",
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ─── Markdown renderer ─── */
function MarkdownContent({ content, color }: { content: string; color: string }) {
  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif", fontSize: "22px", fontWeight: 700,
            color: "#ffffff", marginBottom: "12px", marginTop: "24px",
          }}>{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif", fontSize: "18px", fontWeight: 600,
            color, marginBottom: "10px", marginTop: "20px",
          }}>{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 style={{
            fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", fontWeight: 600,
            color: "rgba(255,255,255,0.85)", marginBottom: "8px", marginTop: "16px",
          }}>{children}</h3>
        ),
        p: ({ children }) => (
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px",
            color: "rgba(255,255,255,0.5)", lineHeight: 1.8, marginBottom: "12px",
          }}>{children}</p>
        ),
        ul: ({ children }) => (
          <ul style={{ listStyleType: "none", paddingLeft: "0", marginBottom: "12px" }}>{children}</ul>
        ),
        li: ({ children }) => (
          <li style={{
            fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px",
            color: "rgba(255,255,255,0.5)", lineHeight: 1.8, paddingLeft: "16px",
            position: "relative", marginBottom: "4px",
          }}>
            <span style={{
              position: "absolute", left: "0", top: "9px",
              width: "5px", height: "5px", borderRadius: "50%",
              background: color, opacity: 0.5,
            }} />
            {children}
          </li>
        ),
        strong: ({ children }) => (
          <strong style={{ color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>{children}</strong>
        ),
        em: ({ children }) => (
          <em style={{ color, fontStyle: "italic" }}>{children}</em>
        ),
        code: ({ children, className }) => {
          const isBlock = className?.includes("language-");
          if (isBlock) {
            return (
              <code className={className} style={{
                display: "block", fontFamily: "'JetBrains Mono', monospace", fontSize: "12px",
                color: "rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px",
                padding: "14px", marginBottom: "12px", overflowX: "auto", lineHeight: 1.6,
              }}>{children}</code>
            );
          }
          return (
            <code style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: "12px",
              color, background: `${color}12`, border: `1px solid ${color}20`,
              borderRadius: "4px", padding: "1px 6px",
            }}>{children}</code>
          );
        },
        pre: ({ children }) => <pre style={{ marginBottom: "12px" }}>{children}</pre>,
        blockquote: ({ children }) => (
          <blockquote style={{
            borderLeft: `3px solid ${color}50`, paddingLeft: "16px",
            marginBottom: "12px", marginLeft: "0",
          }}>{children}</blockquote>
        ),
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer"
            style={{ color, textDecoration: "underline", textUnderlineOffset: "3px" }}>
            {children}
          </a>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

/* ─── Grid card ─── */
function ProjectCard({ project, index, visible, language, onExpand }: {
  project: any;
  index: number;
  visible: boolean;
  language: string;
  onExpand: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isFeatured = index === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.15 + index * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onExpand}
      className={`relative group rounded-2xl overflow-hidden ${isFeatured ? "md:col-span-2 md:row-span-2" : ""}`}
      style={{
        background: hovered ? project.accent : "rgba(255,255,255,0.025)",
        border: `1px solid ${hovered ? project.color + "30" : "rgba(255,255,255,0.06)"}`,
        padding: isFeatured ? "40px" : "28px",
        cursor: "none",
        transition: "background 0.35s, border-color 0.35s, box-shadow 0.35s",
        boxShadow: hovered ? `0 0 40px ${project.accent}` : "none",
      }}
    >
      {/* Glow */}
      <div className="absolute top-0 right-0 pointer-events-none" style={{
        width: "200px", height: "200px",
        background: `radial-gradient(circle at top right, ${project.accent} 0%, transparent 70%)`,
        opacity: hovered ? 1 : 0, transition: "opacity 0.35s",
      }} />

      {/* Number */}
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: isFeatured ? "13px" : "11px",
        color: project.color, letterSpacing: "0.1em", marginBottom: "16px", opacity: 0.7,
      }}>{project.number}</div>

      {/* Title + links */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: isFeatured ? "clamp(22px, 2.5vw, 32px)" : "18px",
          fontWeight: 700, color: "#ffffff", lineHeight: 1.2,
        }}>{project.title}</h3>
        <div className="flex items-center gap-2 flex-shrink-0 mt-1">
          {project.github && project.github !== "#" && (
            <a href={project.github} className="p-2 rounded-lg" style={{
              background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)",
              opacity: hovered ? 1 : 0, transition: "opacity 0.2s",
            }} onClick={(e) => e.stopPropagation()}><Github size={15} /></a>
          )}
          <a href={project.link} target="_blank" rel="noopener noreferrer"
            className="p-2 rounded-lg" style={{
              background: hovered ? project.accent : "rgba(255,255,255,0.05)",
              color: project.color, border: `1px solid ${hovered ? project.border : "transparent"}`,
              opacity: hovered ? 1 : 0.4, transition: "all 0.2s",
            }} onClick={(e) => e.stopPropagation()}><ArrowUpRight size={15} /></a>
        </div>
      </div>

      <p style={{
        fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px",
        color: "rgba(255,255,255,0.45)", lineHeight: 1.75, marginBottom: "20px",
      }}>{project.desc}</p>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-auto">
        {project.tags.map((tag: any) => (
          <span key={tag} className="px-3 py-1 rounded-full" style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: "11px",
            color: project.color, background: `${project.color}12`, border: `1px solid ${project.color}25`,
          }}>{tag}</span>
        ))}
      </div>

      {/* Ask about — absolute */}
      <div style={{
        position: "absolute", bottom: isFeatured ? "40px" : "28px",
        right: isFeatured ? "40px" : "28px",
        opacity: hovered ? 1 : 0, transition: "opacity 0.25s",
        pointerEvents: hovered ? "auto" : "none",
      }}>
        <AskAboutButton subject={project.title} language={language} color={project.color}
          section="project"
          context={`Titre: ${project.title}\nDescription: ${project.desc || ""}\nTechnologies: ${(project.tags || []).join(", ")}`}
        />
      </div>

      {/* Featured badge */}
      {isFeatured && (
        <div className="absolute top-5 right-5" style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: "10px",
          color: project.color, background: `${project.color}15`,
          border: `1px solid ${project.color}30`, padding: "4px 10px",
          borderRadius: "999px", letterSpacing: "0.1em",
        }}>FEATURED</div>
      )}

      {/* Click hint */}
      {project.long_description && (
        <div className="absolute bottom-0 left-0 w-full flex items-center justify-center pointer-events-none"
          style={{ paddingBottom: "10px", opacity: hovered ? 1 : 0, transition: "opacity 0.3s" }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: "10px",
            color: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", gap: "4px",
          }}>
            <ChevronDown size={12} />
            {language === "en" ? "click to expand" : "cliquer pour détails"}
          </span>
        </div>
      )}
    </motion.div>
  );
}

/* ─── Expanded detail panel ─── */
function ExpandedPanel({ project, onClose, onSwitch, otherProjects, language }: {
  project: any;
  onClose: () => void;
  onSwitch: (p: any) => void;
  otherProjects: any[];
  language: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* Card container */}
      <div
        className="rounded-2xl overflow-hidden relative"
        style={{
          background: project.accent,
          border: `1px solid ${project.color}30`,
        }}
      >
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 w-full" style={{
          height: "2px",
          background: `linear-gradient(90deg, ${project.color}, ${project.color}40, transparent)`,
        }} />

        {/* Glow */}
        <div className="absolute top-0 right-0 pointer-events-none" style={{
          width: "250px", height: "250px",
          background: `radial-gradient(circle at top right, ${project.color}10 0%, transparent 70%)`,
        }} />

        <div className="p-8 md:p-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1, x: -2 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center w-9 h-9 rounded-xl"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.6)", cursor: "none", flexShrink: 0,
                }}
              >
                <ArrowLeft size={16} />
              </motion.button>
              <div>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: "11px",
                  color: project.color, letterSpacing: "0.1em", opacity: 0.7,
                }}>{project.number}</span>
                <h3 style={{
                  fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(20px, 2.5vw, 28px)",
                  fontWeight: 700, color: "#ffffff", lineHeight: 1.2,
                }}>{project.title}</h3>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 mt-1">
              <AskAboutButton subject={project.title} language={language} color={project.color}
                section="project"
                context={`Titre: ${project.title}\nDescription: ${project.desc || ""}\nTechnologies: ${(project.tags || []).join(", ")}\nDescription longue: ${project.long_description || ""}`}
              />
              {project.github && project.github !== "#" && (
                <a href={project.github} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-lg" style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.6)",
                  }}><Github size={15} /></a>
              )}
              {project.link && project.link !== "#" && (
                <a href={project.link} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-lg" style={{
                    background: `${project.color}15`, border: `1px solid ${project.color}30`,
                    color: project.color,
                  }}><ArrowUpRight size={15} /></a>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {project.tags.map((tag: any) => (
              <span key={tag} className="px-3 py-1 rounded-full" style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: "11px",
                color: project.color, background: `${project.color}12`,
                border: `1px solid ${project.color}25`,
              }}>{tag}</span>
            ))}
          </div>

          {/* Short desc */}
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px",
            color: "rgba(255,255,255,0.5)", lineHeight: 1.75, marginBottom: "16px",
          }}>{project.desc}</p>

          {/* Long description — fixed height scrollable */}
          {project.long_description && (
            <div style={{ position: "relative" }}>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />
              <div
                ref={contentRef}
                style={{
                  height: "350px",
                  overflowY: "auto",
                  paddingTop: "16px",
                  paddingRight: "8px",
                  scrollbarWidth: "thin",
                  scrollbarColor: `${project.color}25 transparent`,
                }}
              >
                <MarkdownContent content={project.long_description} color={project.color} />
              </div>
              {/* Scroll fade */}
              <div style={{
                position: "absolute", bottom: 0, left: 0, right: 0, height: "50px",
                background: "linear-gradient(transparent, #0a0a10)",
                pointerEvents: "none",
              }} />
            </div>
          )}
        </div>
      </div>

      {/* Other projects — horizontal scroll */}
      <div className="mt-4">
        <div className="flex gap-3 overflow-x-auto pb-2" style={{
          scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.08) transparent",
        }}>
          {otherProjects.map((p: any) => (
            <motion.button
              key={p.id || p.number}
              onClick={() => onSwitch(p)}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="relative flex-shrink-0 rounded-xl text-left"
              style={{
                width: "220px", padding: "18px",
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.06)",
                cursor: "none", transition: "border-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = p.color + "30";
                e.currentTarget.style.background = p.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                e.currentTarget.style.background = "rgba(255,255,255,0.025)";
              }}
            >
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: "10px",
                color: p.color, letterSpacing: "0.1em", marginBottom: "6px", opacity: 0.6,
              }}>{p.number}</div>
              <div style={{
                fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px",
                fontWeight: 600, color: "rgba(255,255,255,0.8)", lineHeight: 1.3, marginBottom: "8px",
              }}>{p.title}</div>
              <div className="flex flex-wrap gap-1">
                {p.tags.slice(0, 3).map((tag: any) => (
                  <span key={tag} className="px-2 py-0.5 rounded-full" style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: "9px",
                    color: p.color, background: `${p.color}10`, border: `1px solid ${p.color}20`,
                  }}>{tag}</span>
                ))}
                {p.tags.length > 3 && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "rgba(255,255,255,0.3)" }}>
                    +{p.tags.length - 3}
                  </span>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Main ─── */
export function Projects({ projects = [], language = "fr" }: { projects?: any[], language?: string }) {
  const { ref, visible } = useInView(0.05);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const displayProjects = projects.length > 0
    ? projects.map((p: any, index: number) => ({
      ...p,
      _idx: index,
      number: `0${index + 1}`.slice(-2),
      desc: p.short_description || p.long_description,
      tags: p.technologies ? p.technologies.map((t: any) => t.name || t) : [],
      color: PALETTE[index % PALETTE.length].color,
      accent: PALETTE[index % PALETTE.length].accent,
      border: PALETTE[index % PALETTE.length].border,
      link: p.live_url || "#",
      github: p.github_url || "#",
    }))
    : FALLBACK_PROJECTS.map((p, i) => ({
      ...p, _idx: i,
      number: `0${i + 1}`.slice(-2),
      desc: p.short_description,
      tags: p.technologies.map((t) => t.name),
      color: PALETTE[i].color, accent: PALETTE[i].accent, border: PALETTE[i].border,
      link: p.live_url || "#", github: p.github_url || "#",
    }));

  const expandedProject = expandedId !== null
    ? displayProjects.find((p: any) => (p.id || p._idx) === expandedId)
    : null;

  const otherProjects = expandedProject
    ? displayProjects.filter((p: any) => (p.id || p._idx) !== expandedId)
    : [];

  const handleExpand = (project: any) => {
    setExpandedId(project.id || project._idx);
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleClose = () => setExpandedId(null);

  const handleSwitch = (project: any) => {
    setExpandedId(project.id || project._idx);
  };

  return (
    <section
      id="projects"
      ref={(el) => { (ref as any).current = el; (sectionRef as any).current = el; }}
      className="py-24 md:py-36"
      style={{ background: "#050508" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={visible ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-16"
        >
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: "11px",
            color: "#22d3ee", letterSpacing: "0.15em",
          }}>
            {language === "en" ? "02 // PROJECTS" : "02 // PROJETS"}
          </span>
          <div className="flex-1 h-px" style={{ background: "rgba(34,211,238,0.15)" }} />
        </motion.div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.6 }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 700,
              color: "#ffffff", letterSpacing: "-0.02em", lineHeight: 1.1,
            }}
          >
            {language === "en" ? "Recent " : "Projets "}
            <span style={{ color: "#22d3ee" }}>{language === "en" ? "projects" : "récents"}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: 20 }}
            animate={visible ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-left md:text-right"
            style={{
              fontFamily: "'Space Grotesk', sans-serif", fontSize: "16px",
              color: "rgba(255,255,255,0.5)", maxWidth: "300px",
            }}
          >
            {language === "en" ? "Selected works and recent assignments." : "Quelques-unes de mes réalisations."}
          </motion.p>
        </div>

        {/* Expanded panel — instant swap */}
        {expandedProject && (
          <ExpandedPanel
            project={expandedProject}
            onClose={handleClose}
            onSwitch={handleSwitch}
            otherProjects={otherProjects}
            language={language}
          />
        )}

        {/* Grid — instant hide/show, no animation delay */}
        {!expandedProject && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-auto">
            {displayProjects.map((project: any, i: number) => (
              <ProjectCard
                key={project.id || i}
                project={project}
                index={i}
                visible={visible}
                language={language}
                onExpand={() => handleExpand(project)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
